import { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckCircle, FiClipboard, FiPlus, FiTrash2 } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import InventoryProductPicker from './InventoryProductPicker';

const emptyRow = () => ({
  variantId: "",
  countedQuantity: "",
});

const number = (value) => Number(value || 0);

const quantity = (value) =>
  number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });

const today = () => new Date().toISOString().slice(0, 10);

const variantLabel = (balance) =>
  [
    balance.variant?.product?.name,
    balance.variant?.color?.name,
    balance.variant?.size?.name,
    balance.variant?.sku,
  ]
    .filter(Boolean)
    .join(" · ");

export default function StockCountPage() {
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [stores, setStores] = useState([]);
  const [balances, setBalances] = useState([]);
  const [movements, setMovements] = useState([]);

  const [storeId, setStoreId] = useState("");
  const [countType, setCountType] = useState("CYCLE_COUNT");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadData = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const [storesData, balancesData, movementsData] = await Promise.all([
        apiRequest("/inventory/stores", { accessToken }),
        apiRequest("/inventory/balances", { accessToken }),
        apiRequest("/inventory/movements", { accessToken }),
      ]);

      setStores(storesData || []);
      setBalances(balancesData || []);
      setMovements(movementsData || []);
    } catch (err) {
      setError(err.message || "Unable to load stock count data.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadData();
  }, [isAuthLoading, accessToken, loadData]);

  const storeBalances = useMemo(
    () => balances.filter((balance) => balance.storeId === storeId),
    [balances, storeId],
  );

  const balanceByVariantId = useMemo(
    () => new Map(storeBalances.map((balance) => [balance.variantId, balance])),
    [storeBalances],
  );

  const stats = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const todayCountMovements = movements.filter(
      (movement) =>
        ["STOCK_COUNT_GAIN", "STOCK_COUNT_LOSS"].includes(movement.type) &&
        new Date(movement.occurredAt) >= todayStart,
    );

    const countReferences = new Set(
      todayCountMovements
        .map((movement) => movement.referenceId)
        .filter(Boolean),
    );

    const totalVariance = todayCountMovements.reduce(
      (total, movement) => total + Math.abs(number(movement.quantityChange)),
      0,
    );

    const netVariance = todayCountMovements.reduce(
      (total, movement) => total + number(movement.quantityChange),
      0,
    );

    return {
      countsToday: countReferences.size,
      reconciliations: todayCountMovements.length,
      totalVariance,
      netVariance,
    };
  }, [movements]);

  const removeRow = (index) => {
    if (rows.length === 1) {
      setError("A stock count requires at least one product.");
      return;
    }

    setRows((previous) => previous.filter((_, rowIndex) => rowIndex !== index));
  };

  const updateRow = (index, field, value) => {
    setRows((previous) =>
      previous.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const changeStore = (value) => {
    setStoreId(value);
    setRows([emptyRow()]);
    setError("");
    setSuccess("");
  };

  const submitCount = async () => {
    setError("");
    setSuccess("");

    if (!storeId) {
      setError("Select a store or warehouse.");
      return;
    }

    const variantIds = rows.map((row) => row.variantId);

    if (variantIds.some((variantId) => !variantId)) {
      setError("Select a product variant in every row.");
      return;
    }

    if (new Set(variantIds).size !== variantIds.length) {
      setError("The same variant cannot be counted more than once.");
      return;
    }

    if (
      rows.some(
        (row) => row.countedQuantity === "" || number(row.countedQuantity) < 0,
      )
    ) {
      setError("Enter a physical count of zero or more for every row.");
      return;
    }

    try {
      setSaving(true);

      const countLabel = countType.replaceAll("_", " ");

      const response = await apiRequest("/inventory/stock-counts/bulk", {
        method: "POST",
        accessToken,
        body: {
          storeId,
          note: `[${countLabel}]${notes.trim() ? ` ${notes.trim()}` : ""}`,
          items: rows.map((row) => ({
            variantId: row.variantId,
            countedQuantity: number(row.countedQuantity),
          })),
        },
      });

      setSuccess(
        `Stock count reconciled successfully. Reference: ${response.stockCountReferenceId}`,
      );

      setRows([emptyRow()]);
      setNotes("");

      await loadData();
    } catch (err) {
      setError(err.message || "Unable to submit stock count.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {pickerOpen && <InventoryProductPicker open onClose={() => setPickerOpen(false)} storeId={storeId} balances={balances} accessToken={accessToken} onAdd={selected => setRows(current => {
        const existing = current.filter(row => row.variantId);
        const ids = new Set(existing.map(row => row.variantId));
        return [...existing, ...selected.filter(row => !ids.has(row.variantId)).map(row => ({ ...emptyRow(), variantId: row.variantId }))];
      })} />}
      <div className="mb-8">
        <p className="text-sm text-gray-500">Inventory / Stock Count</p>

        <h1 className="mt-1 text-3xl font-bold">Stock Count</h1>

        <p className="mt-2 text-gray-500">
          Verify physical stock and reconcile inventory variances.
        </p>
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <Card title="Counts Today" value={stats.countsToday} />

        <Card
          title="Reconciliations Today"
          value={stats.reconciliations}
          color="text-primary-700"
        />

        <Card
          title="Variance Found"
          value={quantity(stats.totalVariance)}
          color="text-orange-600"
        />

        <Card
          title="Net Variance"
          value={`${stats.netVariance > 0 ? "+" : ""}${quantity(
            stats.netVariance,
          )}`}
          color={
            stats.netVariance === 0
              ? "text-green-600"
              : stats.netVariance > 0
                ? "text-blue-600"
                : "text-red-600"
          }
        />
      </div>

      {error && (
        <div className="mb-6 rounded-2xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-2xl bg-green-50 p-4 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-5 text-lg font-semibold">Count Information</h2>

        <div className="grid gap-4 lg:grid-cols-4">
          <input
            value="Generated when count is submitted"
            readOnly
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-500"
          />

          <input
            type="date"
            value={today()}
            readOnly
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-500"
          />

          <select
            value={storeId}
            disabled={loading}
            onChange={(event) => changeStore(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">Select store or warehouse</option>

            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name} · {store.type}
              </option>
            ))}
          </select>

          <select
            value={countType}
            onChange={(event) => setCountType(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="CYCLE_COUNT">Cycle Count</option>
            <option value="FULL_COUNT">Full Count</option>
            <option value="ANNUAL_AUDIT">Annual Audit</option>
          </select>
        </div>
      </div>

      <div className="mb-6 overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 p-6">
          <div>
            <h2 className="text-lg font-semibold">Counted Products</h2>

            <p className="mt-1 text-sm text-gray-500">
              Enter the actual physical quantity at this location.
            </p>
          </div>

          <button
            type="button"
            disabled={!storeId}
            onClick={() => setPickerOpen(true)}
            className="flex h-10 items-center gap-2 rounded-xl bg-primary-700 px-4 text-white disabled:opacity-50"
          >
            <FiPlus />
            Find products
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Product Variant
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  System Stock
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Physical Count
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Variance
                </th>

                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => {
                const balance = balanceByVariantId.get(row.variantId);
                const systemStock = number(balance?.quantityOnHand);
                const physicalStock = number(row.countedQuantity);
                const variance = physicalStock - systemStock;

                return (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="min-w-80 px-6 py-4">
                      <select
                        value={row.variantId}
                        disabled={!storeId}
                        onChange={(event) =>
                          updateRow(index, "variantId", event.target.value)
                        }
                        className="h-11 w-full rounded-xl border border-gray-200 px-4"
                      >
                        <option value="">Select product variant</option>

                        {storeBalances.map((balanceItem) => (
                          <option
                            key={balanceItem.variantId}
                            value={balanceItem.variantId}
                          >
                            {variantLabel(balanceItem)}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td className="px-6 py-4 font-semibold text-gray-700">
                      {row.variantId ? quantity(systemStock) : "—"}
                    </td>

                    <td className="px-6 py-4">
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={row.countedQuantity}
                        disabled={!row.variantId}
                        onChange={(event) =>
                          updateRow(
                            index,
                            "countedQuantity",
                            event.target.value,
                          )
                        }
                        className="h-11 w-32 rounded-xl border border-gray-200 px-4"
                      />
                    </td>

                    <td className="px-6 py-4">
                      {row.variantId && row.countedQuantity !== "" ? (
                        <span
                          className={`font-bold ${
                            variance === 0
                              ? "text-green-600"
                              : variance > 0
                                ? "text-blue-600"
                                : "text-red-600"
                          }`}
                        >
                          {variance > 0 ? "+" : ""}
                          {quantity(variance)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => removeRow(index)}
                        className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {storeId && storeBalances.length === 0 && (
          <div className="border-t border-gray-100 p-5 text-sm text-gray-500">
            This location has no current inventory balances to count.
          </div>
        )}
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-4 font-semibold">Count Notes</h2>

        <textarea
          rows="4"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Add notes regarding stock verification..."
          className="w-full rounded-xl border border-gray-200 p-4 outline-none focus:border-primary-700"
        />
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => {
            setRows([emptyRow()]);
            setNotes("");
            setError("");
            setSuccess("");
          }}
          className="h-11 rounded-xl border border-gray-200 px-6"
        >
          Clear Form
        </button>

        <button
          type="button"
          disabled={saving || loading}
          onClick={submitCount}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-6 text-white disabled:opacity-60"
        >
          <FiClipboard />
          {saving ? "Reconciling…" : "Submit Count"}
        </button>
      </div>

      <p className="mt-4 text-center text-xs text-gray-400">
        Submitting a count immediately posts stock-count gain or loss movements.
        Draft and approval workflow are not part of the current ledger design.
      </p>
    </div>
  );
}

function Card({ title, value, color = "text-gray-900" }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <h3 className={`mt-2 text-3xl font-bold ${color}`}>{value}</h3>
    </div>
  );
}
